import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { startWith, tap } from 'rxjs';
import { Pet } from '../../interfaces/pet.interfaces';
import { PetService } from '../../services/pet.service';
import { PaginationComponent } from '../ui/pagination/pagination.component';

@Component({
  selector: 'app-pet-view',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, PaginationComponent],
  templateUrl: './pet-view.component.html',
  styleUrl: './pet-view.component.scss'
})
export class PetViewComponent implements OnInit {
  private apiService = inject(PetService);

  data: Pet[] = [];
  filteredData: Pet[] = [];
  filter = new FormControl('', { nonNullable: true });

  page = 1;
  pageSize = 6;

  get pagedPets(): Pet[] {
    const start = (this.page - 1) * this.pageSize;
    return this.filteredData.slice(start, start + this.pageSize);
  }

  private readonly _destroyRef: DestroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.apiService
      .getAllPets()
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((pets: Pet[]) => {
          this.data = pets || [];
          this.filteredData = pets || [];
        }),
      )
      .subscribe();

    this.filter.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        startWith(''),
        tap((term: string) => {
          this.filteredData = this.search(term);
          this.page = 1;
        }),
      )
      .subscribe();
  }

  private search(text: string): Pet[] {
    const term = text.trim().toLowerCase();
    if (!term) return [...this.data];

    return this.data.filter((pet: Pet) => {
      return (
        (pet.petName || '').toLowerCase().includes(term) ||
        (pet.status || '').toLowerCase().includes(term) ||
        (pet.responsibleOwner || '').toLowerCase().includes(term) ||
        (pet.email || '').toLowerCase().includes(term) ||
        (pet.phoneNumbers || '').toLowerCase().includes(term) ||
        (pet.folio || '').toLowerCase().includes(term) ||
        (pet.petRace || '').toLowerCase().includes(term)
      );
    });
  }
}
